/**
 * What a session leaves in this tab, emptied as it ends — the user signs out,
 * or the session expires — so whoever signs in next in it sees none of it.
 *
 * Signing out cleared the auth store and the query cache, and nothing else: a
 * moderator's last call (its player, match, world and team) and the voice
 * monitor's transcript of what the students said stayed in their stores, for
 * the next sign-in to read; a question still asked (a confirm's "Delete the
 * world?") could be answered by the next user, and run with their token; and
 * the last session's toasts stayed up over the sign-in screen.
 *
 * Each store a session fills registers how it empties itself, beside it, as its
 * module loads (`forgetAtSessionEnd`) — one never loaded holds nothing — and
 * the query cache does the same. It sits in lib/, which a component's store
 * may import as a feature's may, so the sign-out reaches no component: the
 * design rule store-kept-past-session-end holds every zustand store in src to
 * a registration of its own.
 *
 * What the session must still say goes before a sign-out, while its token
 * works (`leaveAtSignOut`): a moderator who signed out mid-call left the
 * player alone in a silent call, since only the call window's close told the
 * player's match it was over. A session that expired says nothing — its token
 * is refused.
 */

/** How each store empties itself, registered as its module loads. */
const forgetters = new Set<() => void>();

/** Registers how a store empties itself when a session ends: call it once, beside the store. */
export function forgetAtSessionEnd(forget: () => void): void {
  forgetters.add(forget);
}

/** Empties everything a session filled: every store registered, and the query cache. */
export function forgetSession(): void {
  for (const forget of forgetters) forget();
}

/** What each piece must still say as the user signs out, registered as its module loads. */
const leavers = new Set<() => Promise<unknown>>();

/** How long a sign-out waits for what the session must still say: a server that does not answer keeps no one signed in. */
const LEAVE_WAIT_MS = 3000;

/** Registers what a session must say before the user signs out, while its token still works: call it once, beside what it ends. */
export function leaveAtSignOut(leave: () => Promise<unknown>): void {
  leavers.add(leave);
}

/** Says what every registered piece must say before a sign-out, waiting at most LEAVE_WAIT_MS; a failure is no reason to stay signed in. */
export async function leaveSession(): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const waited = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, LEAVE_WAIT_MS);
  });
  await Promise.race([Promise.allSettled([...leavers].map(async (leave) => leave())), waited]);
  clearTimeout(timer);
}
